import { useMemo } from 'react'
import type { FeedFormat } from '../api/types'

// «Новое с прошлого захода» — у каждой вкладки ленты своё. В localStorage —
// время прошлого захода во вкладку (на пользователя и формат). В
// sessionStorage — точка отсчёта текущей сессии: она не сдвигается от
// перезагрузки страницы, переходов внутри приложения и переключения вкладок.

const FORMATS: FeedFormat[] = ['video', 'text']
const seenKey = (userId: number, format: FeedFormat) => `cf2_feed_seen_${userId}_${format}`
const baseKey = (userId: number, format: FeedFormat) => `cf2_feed_base_${userId}_${format}`
// До вкладок время захода было одно на ленту: оно служит отсчётом обеим вкладкам.
const legacySeenKey = (userId: number) => `cf2_feed_seen_${userId}`

function storedSeen(userId: number, format: FeedFormat): string | null {
  return localStorage.getItem(seenKey(userId, format)) ?? localStorage.getItem(legacySeenKey(userId))
}

/** Отсчёт вкладки без отметки захода: для счётчика соседней вкладки. */
function peekBaseline(userId: number, format: FeedFormat): string | null {
  try {
    const kept = sessionStorage.getItem(baseKey(userId, format))
    if (kept !== null) return kept || null
    return storedSeen(userId, format)
  } catch {
    return null
  }
}

/** Человек открыл вкладку: отсчёт сессии фиксируется, время захода — сейчас. */
function enterBaseline(userId: number, format: FeedFormat): string | null {
  try {
    const kept = sessionStorage.getItem(baseKey(userId, format))
    if (kept !== null) return kept || null
    const previous = storedSeen(userId, format)
    sessionStorage.setItem(baseKey(userId, format), previous ?? '')
    localStorage.setItem(seenKey(userId, format), new Date().toISOString())
    return previous
  } catch {
    // хранилище недоступно — без меток «новое»
    return null
  }
}

/** ISO-время прошлого захода в каждую вкладку или null, если в ней впервые.
 *  Открытая вкладка отмечается как просмотренная (один раз за сессию). `active`
 *  null — не отмечается никакая: в «Добавленных» заход в «Видео» сбросил бы
 *  «+N новых» у «Авто». */
export function useFeedBaselines(userId: number, active: FeedFormat | null): Record<FeedFormat, string | null> {
  return useMemo(() => {
    const out = {} as Record<FeedFormat, string | null>
    for (const f of FORMATS) out[f] = f === active ? enterBaseline(userId, f) : peekBaseline(userId, f)
    return out
  }, [userId, active])
}

export function isNewSince(firstSeen: string | null, baseline: string | null): boolean {
  if (!baseline || !firstSeen) return false
  return new Date(firstSeen).getTime() > new Date(baseline).getTime()
}
